import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {ErrorBoundary} from '@sentry/react';
import type {ProductRoadmapEmbedConfigResponse} from 'client';
import {t} from 'i18next';
import {lazy, Suspense} from 'react';

import {getConfluenceEmbedConfig} from './confluenceEmbed';
import {PaperRoadmapEmbed} from './PaperRoadmapEmbed';
import styles from './ProductRoadmap.module.css';
import {RoadmapEmbedLoadingFrame} from './RoadmapEmbedLoadingFrame';

const ConfluenceRoadmapEmbed = lazy(() =>
  import('./ConfluenceRoadmapEmbed').then((module) => ({default: module.ConfluenceRoadmapEmbed}))
);

export const RoadmapDocumentEmbed = ({
  data,
  usePaperEmbed = true,
}: {
  data: ProductRoadmapEmbedConfigResponse;
  usePaperEmbed?: boolean;
}) => {
  if (data.dropbox_embed_app_key && usePaperEmbed) {
    return (
      <PaperRoadmapEmbed
        appKey={data.dropbox_embed_app_key}
        url={data.open_url}
        title={data.title}
      />
    );
  }

  if (data.dropbox_embed_app_key || data.iframe_url) {
    return (
      <Box className={styles.productRoadmapEmbedFrameShell}>
        <iframe
          className={styles.productRoadmapEmbedFrame}
          data-testid="product-roadmap-iframe"
          title={data.title}
          src={data.dropbox_embed_app_key ? data.open_url : data.iframe_url!}
          loading="eager"
          referrerPolicy={
            data.dropbox_embed_app_key ? 'strict-origin-when-cross-origin' : 'no-referrer'
          }
          allowFullScreen
        />
      </Box>
    );
  }

  const confluenceConfig = getConfluenceEmbedConfig(data.open_url);
  if (confluenceConfig) {
    return (
      <ErrorBoundary
        key={`${confluenceConfig.parentProduct}:${data.open_url}`}
        fallback={
          <Box padding="24" role="status">
            <Text>{t('product_roadmap_open_source_document')}</Text>
          </Box>
        }
      >
        <Suspense fallback={<RoadmapEmbedLoadingFrame />}>
          <ConfluenceRoadmapEmbed {...confluenceConfig} title={data.title} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <Box padding="24">
      <Text>{t('product_roadmap_open_source_document')}</Text>
    </Box>
  );
};
